/**
 * @fileOverview Implements a fetch function that can handle multipart/mixed responses.
 * The multipart response is expected to follow the GraphQL multipart request spec.
 *
 * See https://github.com/graphql/graphql-over-http/blob/main/rfcs/IncrementalDelivery.md#content-type-multipartmixed
 * See https://github.com/graphql/graphql-over-http/blob/main/spec/GraphQLOverHTTP.md#multipart-request-format
 */
import merge from 'lodash/merge';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';
import { Observable, type RequestParameters, type Variables } from 'relay-runtime';
import { NO_CONTENT } from '@jira/platform__http-status-codes/src/http-status-codes.tsx';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import { applyObservabilityHeaders } from '@atlassian/jira-fetch/src/index.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import { handleNetworkErrors, handleErrors } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { GraphQLMultipartResponsePayload } from './types.tsx';

function getBoundary(contentType: string): string | null {
	const parts = contentType.split('boundary=');
	if (parts.length < 2) return null;
	return parts[1].trim();
}

export function processChunk(
	chunk: string,
	boundaryString: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): string {
	const parts = chunk.split(boundaryString);
	let incompleteChunk = '';

	// Process all complete parts (except the last one which might be incomplete)
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i].trim();

		// Skip empty parts and encapsulation boundaries
		if (part && !part.startsWith('--')) {
			// Split content-type header and body
			const [part1, part2] = part.split(/\r?\n\r?\n/);

			// when response content has boundary string, part1 is the actual response
			// when response content does not have boundary string, part2 is the actual response
			const body = part2 ?? part1;

			if (body) {
				try {
					const jsonData = JSON.parse(incompleteChunk + body);
					observer.next(jsonData);
					incompleteChunk = '';
				} catch (error) {
					// add boundary string back to response for cases when response content has boundary string
					// no error is thrown because next part might be a valid JSON object
					incompleteChunk += body + boundaryString;
				}
			}
		}
	}

	// Return the last part which might be incomplete
	return incompleteChunk + parts[parts.length - 1];
}

export async function processStream(
	reader: AsyncGenerator<string, void>,
	boundary: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): Promise<void> {
	const boundaryString = `--${boundary}`;
	let incompleteChunk = '';

	// Process chunks until the stream is done
	for await (const newText of reader) {
		const combinedChunk = incompleteChunk + newText;

		// Process the combined chunk and get any incomplete part
		incompleteChunk = processChunk(combinedChunk, boundaryString, observer);
	}

	// Process any remaining data
	if (incompleteChunk.length > 0) {
		processChunk(incompleteChunk, boundaryString, observer);
	}
	observer.complete();
}

function handleMultipartResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	const contentType = response.headers.get('content-type');
	const boundary = getBoundary(contentType || '');

	if (!boundary) {
		observer.error(new Error('Invalid multipart boundary'));
		return;
	}

	const decoder = new TextDecoder('utf-8');

	try {
		// Depending on the version of Node we're running in, the Response object may not implement ReadableStream.
		// Here, we treat the response as an async iterator, since both node-fetch and browser implement the response body as a stream.
		// We wrap that iteration in an async generator, which lets us
		async function* streamReader() {
			// @ts-expect-error a Response object in Node is streamable
			for await (const chunk of response.body) {
				const newText = decoder.decode(chunk, { stream: true });
				yield newText;
			}
		}
		processStream(streamReader(), boundary, observer).catch((error) => observer.error(error));
	} catch (e) {
		observer.error(new Error('Failed to read from response body', { cause: e }));
	}
}

function handleJsonResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	response
		.json()
		.then((data) => observer.next(data))
		.catch((error) => observer.error(error))
		.finally(() => observer.complete());
}

function handleTextResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	response
		.text()
		.then((data) => {
			const maybeJson = data ? JSON.parse(data) : null;
			observer.next(maybeJson);
			observer.complete();
		})
		.catch((error) => {
			observer.error(
				new Error(
					`Error while parsing text response (Content-Type: ${response?.headers?.get('content-type')}): ${error instanceof Error ? error.message : String(error)}`,
				),
			);
		});
}

function processMultipartPromise(
	source: () => Promise<Response>,
): Observable<GraphQLMultipartResponsePayload> {
	// Cache the result of the source promise to avoid calling it multiple times.
	const result = source();
	// Wrap an observer around the result to handle the response.
	return Observable.create((observer) => {
		result
			.then((response) => {
				// Import log at the top of the file if not already imported
				// import log from '@atlassian/jira-common-util-logging/src/log.tsx';

				if (response.status === NO_CONTENT) {
					observer.complete();
					return;
				}

				const contentType = response.headers.get('content-type') || '';

				if (
					contentType.includes('application/json') ||
					contentType.includes('application/graphql-response+json')
				) {
					handleJsonResponse(response, observer);
				} else if (contentType.includes('text/plain')) {
					handleTextResponse(response, observer);
				} else if (contentType.includes('multipart/mixed')) {
					handleMultipartResponse(response, observer);
				} else {
					if (fg('jira-relay-verbose-logging')) {
						// Log all response headers using Jira's logging system
						const allHeaders: Record<string, string> = {};
						response.headers.forEach((value, key) => {
							allHeaders[key] = value;
						});
						logger.unsafeErrorWithCustomerData('relay.multipart.response', 'Response headers', {
							...allHeaders,
						});
					}

					if (fg('jira-relay-text-response-for-default-content-type')) {
						handleTextResponse(response, observer);
					} else {
						observer.error(new Error(`Unsupported content type: ${contentType}`));
					}
				}
			})
			.catch((error) => observer.error(error));
	});
}

function withErrorHandling(source: Promise<Response>): Promise<Response> {
	return source.then(handleErrors).catch(handleNetworkErrors);
}

const relayDeferAcceptableFormats = [
	'application/graphql-response+json',
	'application/json',
	// there must not be spacing between `multipart/mixed` and `deferSpec=20220824` since the backend assumes no spacing
	'multipart/mixed;deferSpec=20220824',
].join(', ');

function buildGraphqlFetch(url: string, options?: RequestInit): () => Promise<Response> {
	const postOptions = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: relayDeferAcceptableFormats,
		},
	};

	const composedOptions = merge(getDefaultOptions(url), options, postOptions);
	const fetchUrl = getReroutableURL(url);
	const fetchHeaders = applyObservabilityHeaders(url, composedOptions);

	return () => fetch(fetchUrl, fetchHeaders);
}

const MAX_QUERY_LENGTH = 2000; // Maximum allowed query length
const MAX_HEADERS_SIZE = 20 * 1024; // Maximum header size in bytes (20 KB)

// Calculate the size of the headers
function calculateHeadersSize(headers: HeadersInit): number {
	let totalBytes = 0;
	const textEncoder = new TextEncoder();

	// Iterate over each header entry (name and value)
	for (const [name, value] of Object.entries(headers)) {
		const headerSize = textEncoder.encode(`${name}: ${value}\r\n`).length; // Include CRLF
		totalBytes += headerSize;
	}

	return totalBytes;
}

function shouldUseGetMethod({
	url,
	variables,
	request,
	headers,
}: {
	url: string;
	variables: Variables;
	request: RequestParameters;
	headers?: HeadersInit;
}): {
	isGetRequest: boolean;
	reason?: string;
} {
	if (__SERVER__) {
		return {
			isGetRequest: false,
			reason: 'SSR',
		};
	}

	if (request.operationKind !== 'query') {
		return {
			isGetRequest: false,
			reason: request.operationKind,
		};
	}
	const serializedVariables = JSON.stringify(variables);
	const queryLength = url.length + serializedVariables.length;
	const headersSize = calculateHeadersSize(headers || {});
	return queryLength <= MAX_QUERY_LENGTH && headersSize <= MAX_HEADERS_SIZE
		? { isGetRequest: true }
		: { isGetRequest: false, reason: 'URL length or headers size too large' };
}

type BuildGraphqlSmartFetchParams = {
	url: string;
	options?: RequestInit;
	variables: Variables;
	request: RequestParameters;
	logFallback?: (reason: string) => void;
};

function buildGraphqlSmartFetch({
	url,
	options,
	variables,
	request,
	logFallback,
}: BuildGraphqlSmartFetchParams): () => Promise<Response> {
	const requestOptions = {
		headers: {
			Accept: relayDeferAcceptableFormats,
			'Content-Type': 'application/json',
		},
	};

	const composedOptions = merge(getDefaultOptions(url), options, requestOptions);
	let fetchUrl = getReroutableURL(url);
	const fetchOptions = applyObservabilityHeaders(url, composedOptions);

	const { isGetRequest, reason } = shouldUseGetMethod({
		url: fetchUrl,
		variables,
		request,
		headers: fetchOptions.headers,
	});

	if (isGetRequest) {
		fetchOptions.method = 'GET';

		const params = new URLSearchParams();
		params.append('variables', JSON.stringify(variables));

		fetchUrl += `${fetchUrl.includes('?') ? '&' : '?'}${params.toString()}`;

		delete fetchOptions.body; // Not needed for GET
		if (fetchOptions.headers && 'Content-Type' in fetchOptions.headers) {
			delete fetchOptions.headers['Content-Type']; // Not needed for GET
		}
	} else {
		fetchOptions.method = 'POST';
		// @ts-expect-error - headers are not typed
		fetchOptions.headers['X-Request-Fallback-To-Post-Reason'] = reason;
		if (reason) {
			logFallback?.(reason);
		}
	}

	return () => fetch(fetchUrl, fetchOptions);
}

function fetchMultipartGraphQL(
	url: string,
	options?: RequestInit,
): Observable<GraphQLMultipartResponsePayload> {
	const fetchFn = buildGraphqlFetch(url, options);
	return processMultipartPromise(() => withErrorHandling(fetchFn()));
}

function smartFetchMultipartGraphQL(params: {
	url: string;
	variables: Variables;
	request: RequestParameters;
	headers?: HeadersInit;
}): Observable<GraphQLMultipartResponsePayload> {
	const fetchFn = buildGraphqlSmartFetch(params);
	return processMultipartPromise(() => withErrorHandling(fetchFn()));
}

export {
	buildGraphqlFetch,
	buildGraphqlSmartFetch,
	withErrorHandling,
	processMultipartPromise,

	// These exports only exist for testing purposes.
	// They are not used directly in application code.
	fetchMultipartGraphQL as fetchGraphQL,
	smartFetchMultipartGraphQL,
	calculateHeadersSize,
	shouldUseGetMethod,
};
